import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaHome, FaUser, FaFileContract } from "react-icons/fa";
import { FaFileInvoice } from "react-icons/fa";
import { FaMapLocationDot } from "react-icons/fa6";
import { FaFileInvoiceDollar } from "react-icons/fa";
import { IoDocumentText } from "react-icons/io5";
import { getRole } from "../../utils/AuthStorage";
import { GrUserWorker } from "react-icons/gr";
import { FaNetworkWired } from "react-icons/fa";
import { MdOutlineTaskAlt } from "react-icons/md";
import { getPermissionByPrefix } from "../../utils/permissions";
import { useVendor } from "../../utils/Context";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedVendor } = useVendor();
  const isInitialLoad = useRef(true);

  // Check if NTT is selected
  const isNTTSelected = selectedVendor === "NTT";

  // Navigate to appropriate dashboard when vendor changes
  useEffect(() => {
    if (selectedVendor && selectedVendor !== -1) {
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
        return;
      }
      if (isNTTSelected) {
        navigate("/ntt-dashboard");
      } else {
        navigate("/dashboard");
      }
    }
  }, [selectedVendor]);

  const sidebarLinkStyle = {
    color: "#000",
    textDecoration: "none",
    padding: "8px 0",
    marginBlock: "2px",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
  };

  const activeLinkStyle = {
    ...sidebarLinkStyle,
    background: "#7C2EA0",
    color: "#fff",
    borderRadius: "4px",
  };

  const disabledLinkStyle = {
    ...sidebarLinkStyle,
    opacity: 0.4,
    cursor: "not-allowed",
    pointerEvents: "none",
  };

  const subLinkStyle = {
    ...sidebarLinkStyle,
    fontSize: "13px",
    paddingLeft: "42px",
  };

  const activeSubLinkStyle = {
    ...subLinkStyle,
    color: "#7C2EA0",
    fontWeight: "bold",
  };

  const role = getRole();

  const dashboardOverview = getPermissionByPrefix("dashboard_overview");
  const creditAchievement = getPermissionByPrefix("credit_achievement");
  const staffPermission = getPermissionByPrefix("manage_user");
  const vendorPermission = getPermissionByPrefix("manage_vendor");
  const documentPermission = getPermissionByPrefix("manage_document");
  const locationPermission = getPermissionByPrefix("manage_location");
  const rolePermission = getPermissionByPrefix("manage_roles");
  const documenthubPermission = getPermissionByPrefix("upload_document");

  const showDashboard = dashboardOverview?.is_view || creditAchievement?.is_view;

  // Function to determine if a link should be enabled
  const isLinkEnabled = (linkType) => {
    if (!isNTTSelected) return true;
    // When NTT is selected, only enable Dashboard and Document
    return linkType === "dashboard" || linkType === "document"  || linkType === "employee" || linkType === "invoice" || linkType === "circuit-report";
  };

  return (
    <div
      style={{
        width: isOpen ? "220px" : "0",
        height: "100vh",
        marginTop: "70px",
        color: "#fff",
        overflowX: "hidden",
        position: "fixed",
        top: "0",
        borderRight: "2px solid #ccc",
        left: "0",
        transition: "0.3s",
        display: "flex",
        flexDirection: "column",
        padding: isOpen ? "20px 10px" : "0",
      }}
    >
      {isOpen && (
        <>
          {showDashboard && (
            <>
              <Link 
                to={isNTTSelected ? "/ntt-dashboard" : "/dashboard"}
                style={{ ...sidebarLinkStyle, ...( (location.pathname === "/dashboard" || location.pathname === "/ntt-dashboard" || location.pathname === "/circuit-dashboard" || location.pathname === "/invoice") ? { background: "rgba(124, 46, 160, 0.1)", borderRadius: "4px", color: "#7C2EA0" } : {} ) }}
              >
                <FaHome style={{ marginInline: "10px" }} /> Dashboard
              </Link>
              <div style={{ display: "flex", flexDirection: "column", marginBottom: "10px" }}>
                <Link 
                  to={isNTTSelected ? "/ntt-dashboard" : "/dashboard"} 
                  style={
                    isLinkEnabled("dashboard")
                      ? (location.pathname === "/dashboard" || location.pathname === "/ntt-dashboard" ? activeSubLinkStyle : subLinkStyle)
                      : { ...disabledLinkStyle, paddingLeft: "42px", fontSize: "13px" }
                  }
                >
                  Billing & Discrepancies
                </Link>
                <Link 
                  to="/invoice"
                  style={
                    isLinkEnabled("invoice")
                      ? (location.pathname === "/invoice" ? activeSubLinkStyle : subLinkStyle)
                      : { ...disabledLinkStyle, paddingLeft: "42px", fontSize: "13px" }
                  }
                >
                  Invoice Insights
                </Link>
                <Link 
                  to="/circuit-dashboard"
                  style={
                    isLinkEnabled("circuit-report")
                      ? (location.pathname === "/circuit-dashboard" ? activeSubLinkStyle : subLinkStyle)
                      : { ...disabledLinkStyle, paddingLeft: "42px", fontSize: "13px" }
                  }
                >
                  Circuit Insights
                </Link>
              </div>
            </>
          )}
          
          {(documenthubPermission?.is_view || documentPermission?.is_view) && (
            <>
              <Link
                to="/document-hub"
                style={{ 
                  ...sidebarLinkStyle, 
                  ...( (location.pathname === "/document-hub" || location.pathname === "/document" || location.pathname === "/ntt-document") ? { background: "rgba(124, 46, 160, 0.1)", borderRadius: "4px", color: "#7C2EA0" } : {} ) 
                }}
              >
                <IoDocumentText style={{ marginInline: "10px" }} /> Documents
              </Link>
              <div style={{ display: "flex", flexDirection: "column", marginBottom: "10px" }}>
                {documenthubPermission?.is_view && (
                  <Link 
                    to="/document-hub" 
                    style={
                      isLinkEnabled("document-hub")
                        ? (location.pathname === "/document-hub" ? activeSubLinkStyle : subLinkStyle)
                        : { ...disabledLinkStyle, paddingLeft: "42px", fontSize: "13px" }
                    }
                  >
                    Document Hub
                  </Link>
                )}
                
                {documentPermission?.is_view && (
                  <Link 
                    to={isNTTSelected ? "/ntt-document" : "/document"} 
                    style={
                      isLinkEnabled("document")
                        ? (location.pathname === "/document" || location.pathname === "/ntt-document" ? activeSubLinkStyle : subLinkStyle)
                        : { ...disabledLinkStyle, paddingLeft: "42px", fontSize: "13px" }
                    }
                  >
                    Document
                  </Link>
                )}
              </div>
            </>
          )}
          
          {locationPermission?.is_view && (
            <Link 
              to="/location" 
              style={
                isLinkEnabled("location")
                  ? (location.pathname === "/location" ? activeLinkStyle : sidebarLinkStyle)
                  : disabledLinkStyle
              }
            >
              <FaMapLocationDot style={{ marginInline: "10px" }} /> Locations
            </Link>
          )}

          {vendorPermission?.is_view && (
            <Link 
              to="/vendor" 
              style={
                isLinkEnabled("vendor")
                  ? (location.pathname === "/vendor" ? activeLinkStyle : sidebarLinkStyle)
                  : disabledLinkStyle
              }
            >
              <FaUser style={{ marginInline: "10px" }} /> Vendors
            </Link>
          )}

          {staffPermission?.is_view && (
            <Link 
              to="/employee" 
              style={
                isLinkEnabled("employee")
                  ? (location.pathname === "/employee" ? activeLinkStyle : sidebarLinkStyle)
                  : disabledLinkStyle
              }
            >
              <GrUserWorker style={{ marginInline: "10px" }} /> Users
            </Link>
          )}
          
          {rolePermission?.is_view && (
            <Link 
              to="/role" 
              style={
                isLinkEnabled("role")
                  ? (location.pathname === "/role" ? activeLinkStyle : sidebarLinkStyle)
                  : disabledLinkStyle
              }
            >
              <MdOutlineTaskAlt style={{ marginInline: "10px" }} /> Roles
            </Link>
          )}
        </>
      )}
    </div>
  );
};

export default Sidebar;